<template>
  <loading-contributors v-if="panelLoading || loading" />

  <template v-else>
    <view-section
      v-if="collection"
      :title="$t('curator')"
      :padding-x="false"
      :padding-y="false"
      :class="Classes.TEAM"
    >
      <base-link
        :link-primary="false"
        :to="{
          name: USER_COLLECTION_BROWSE_ROUTE,
          params: { user: collection.submitted_by.name_id },
        }"
      >
        <div
          class="tw-w-full first:tw-pt-4 last:tw-pb-4 tw-p-4 tw-flex tw-items-center tw-justify-between tw-border-b tw-border-white last:tw-border-b-0 tw-space-x-2 game-tile-background-color-hover"
          :class="Classes.CONTRIBUTOR"
        >
          <div class="tw-flex tw-items-center tw-space-x-2">
            <avatar-icon show-status :user="collection.submitted_by" />
            <span class="tw-font-bold tw-text-h6 mod-util-truncate-one-line">
              {{ collection.submitted_by.username }}
            </span>
          </div>
          <follow-button
            simple-mode
            :disabled="collection.submitted_by?.id === getUserId()"
            :user="collection.submitted_by"
          />
        </div>
      </base-link>
    </view-section>
    <view-section
      v-if="!collection || contributors.length"
      :padding-x="false"
      :padding-y="false"
      :title="$t('contributors')"
      :class="Classes.TEAM"
    >
      <base-link
        v-for="c in contributors.slice(0, 5)"
        :key="c.name_id"
        :to="{
          name: USER_MOD_BROWSE_ROUTE,
          params: { user: c.name_id.toLowerCase() },
        }"
        :link-primary="false"
      >
        <div
          class="tw-w-full first:tw-pt-4 last:tw-pb-4 tw-p-4 tw-flex tw-items-center tw-justify-between tw-border-b tw-border-white last:tw-border-b-0 tw-space-x-2 game-tile-background-color-hover"
          :class="Classes.CONTRIBUTOR"
        >
          <div class="tw-flex tw-items-center tw-space-x-2">
            <avatar-icon show-status :user="c" size="md" />
            <span class="tw-font-bold tw-text-h6 mod-util-truncate-one-line">
              {{ c.username }}
            </span>
          </div>
          <follow-button
            simple-mode
            :disabled="c.id === getUserId()"
            :user="c"
          />
        </div>
      </base-link>
      <base-button
        v-if="contributors.length > 5"
        class="tw-p-4 mod-link tw-mx-auto"
        @click="showContributorModal"
      >
        {{ $t('show_more') }}
      </base-button>
    </view-section>
  </template>

  <teleport to="body">
    <base-modal
      v-if="isContributorModalOpen"
      show
      has-close-btn
      size="md"
      :heading="$t('creator_count', { count: contributors?.length })"
      @close:modal="hideContributorModal"
    >
      <div
        v-for="c in contributors"
        :key="c.name_id"
        class="tw-flex tw-items-center tw-justify-between tw-py-2 tw-border-b tw-border-theme-2 last:tw-border-b-0 tw-space-x-2"
      >
        <base-link
          class="tw-flex tw-items-center tw-space-x-1.5"
          hover-primary
          :to="c.to"
        >
          <avatar-icon show-status :user="c" />
          <span class="tw-font-medium mod-util-truncate-one-line">
            {{ c.username }}
          </span>
        </base-link>

        <follow-button
          simple-mode
          position="left"
          :disabled="c?.id === getUserId()"
          :user="c"
        />
      </div>
    </base-modal>
  </teleport>
</template>

<script>
import { useCommentAuthors, useAsync, useRoute, useModal } from '@composables'
import LoadingContributors from '@components/Loading/LoadingContributors.vue'
import FollowButton from '@shared/components/Profile/FollowButton.vue'
import { onMounted, computed, toRefs, watch, ref } from 'vue'
import ViewSection from '@components/View/ViewSection.vue'
import AvatarIcon from '@components/Users/AvatarIcon.vue'
import { getModTeamMembersRequest } from '@services'
import { myTeamsStore, authStore } from '@stores'
import Classes from '@shared/js/config/css.js'
import {
  USER_COLLECTION_BROWSE_ROUTE,
  COLLECTION_VIEW_ROUTE,
  USER_MOD_BROWSE_ROUTE,
} from '@config/routeNames.js'

export default {
  components: {
    LoadingContributors,
    FollowButton,
    ViewSection,
    AvatarIcon,
  },
  props: {
    mod: {
      type: Object,
      default: null,
    },
    collection: {
      type: Object,
      default: null,
    },
    panelLoading: {
      type: Boolean,
      default: false,
    },
    collectionModContributors: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const { mod, collectionModContributors } = toRefs(props)

    const { setAuthors } = useCommentAuthors()
    const { isLoggedIn, getUserId } = authStore()
    const { routeName, getModId } = useRoute()
    const { updateModTeam } = myTeamsStore()
    const modNameId = getModId().value
    const show = ref(false)
    const {
      isModalOpen: isContributorModalOpen,
      hideModal: hideContributorModal,
      showModal: showContributorModal,
    } = useModal()

    const { loading, data, run } = useAsync(() =>
      getModTeamMembersRequest(modNameId)
    )

    setAuthors([])

    onMounted(fetchContributors)

    watch([mod, data], ([_mod, _data]) => {
      if (!_mod || !_data || !isLoggedIn.value) return

      const userId = getUserId()
      const member = _data.data.find((m) => m.user.id === userId)

      if (member) {
        member.is_leader = _mod.submitted_by.id === userId
        updateModTeam(_mod, member)
      }
    })

    async function fetchContributors() {
      if (routeName.value === COLLECTION_VIEW_ROUTE) {
        setAuthors(collectionModContributors.value)
        return
      }

      await run()

      if (data.value?.data.length) {
        setAuthors(
          data.value.data
            .filter((u) => u.invite_pending === 0)
            .map((u) => u.user.name_id)
        )
      }
    }

    const contributors = computed(() => {
      if (collectionModContributors.value.length) {
        const contributorsWithCount = Object.values(
          collectionModContributors.value.reduce((acc, user) => {
            if (!acc[user.id]) {
              acc[user.id] = {
                ...user,
                count: 0,
              }
            }

            acc[user.id].count++
            return acc
          }, {})
        )

        contributorsWithCount.sort((a, b) => b.count - a.count)

        return contributorsWithCount.map((c) => ({
          id: c.id,
          name_id: c.name_id,
          username: c.username,
          avatar: c.avatar,
          date_online: c.date_online,
          to: {
            name: USER_MOD_BROWSE_ROUTE,
            params: {
              user: c.name_id || c.username,
            },
          },
        }))
      }

      if (data.value) {
        const creator = { user: mod.value.submitted_by }
        // Put the creator first
        const users = [
          creator,
          ...data.value.data.filter(
            (user) =>
              user.user.id !== creator.user.id && user.invite_pending === 0
          ),
        ]
        return users.map((c) => ({
          id: c.user.id,
          name_id: c.user.name_id,
          username: c.user.username,
          avatar: c.user.avatar,
          date_online: c.user.date_online,
        }))
      }
      return []
    })

    return {
      USER_COLLECTION_BROWSE_ROUTE,
      isContributorModalOpen,
      USER_MOD_BROWSE_ROUTE,
      hideContributorModal,
      showContributorModal,
      contributors,
      getUserId,
      Classes,
      loading,
      show,
    }
  },
}
</script>
